import React, {PureComponent} from 'react';
import {MuiPickersUtilsProvider, DatePicker, Day} from '@material-ui/pickers';
import MomentUtils  from '@date-io/moment';
import {MaterialUiPickersDate} from '@material-ui/pickers/typings/date';
import {DateTime, TimeZone, DateRange, TimeSpan} from 'util/time';
import moment, {Moment} from 'moment-timezone';
import './index.scss';
import {DateIcon} from "components/Icon";

interface DateRangePickerProps {
  label?: string;
  disabled?: boolean;
  onChange?: (value:DateRange|null)=>void;
  value?: DateRange|null;
  defaultValue?: DateRange|null;
  className?: string;
  minDate?: DateTime;
  maxDate?: DateTime;
  maxDuration?: TimeSpan;
  timeZone?: TimeZone;
}

interface DateRangePickerState {
  startDate: DateTime|null;
  endDate: DateTime|null;
  currentDate: Moment;
  displayedDate: Moment;
}

export default class DateRangePicker extends PureComponent<DateRangePickerProps, DateRangePickerState> {
  state: DateRangePickerState = {
    startDate: null,
    endDate: null,
    currentDate: moment(),
    displayedDate: moment()
  };

  render() {
    let fullClassName = 'date-range-picker';
    if(this.props.className) {
      fullClassName += ' ' + this.props.className;
    }
    if(this.props.disabled) {
      fullClassName += ' disabled';
    }
    const {start, end} = this.getPartialValue();
    const dateValue = start ? start.toDate() : null;
    const inputProps = { endAdornment: <DateIcon /> };
    return <MuiPickersUtilsProvider utils={MomentUtils}>
      <DatePicker
        disableToolbar
        className={fullClassName}
        inputVariant="outlined"
        variant="inline"
        labelFunc={this.renderValue}
        autoOk={!!start && !end}
        disabled={this.props.disabled}
        label={this.props.label}
        value={dateValue}
        renderDay={(day) => this.renderDay(day, start, end)}
        shouldDisableDate={this.shouldDisableDate}
        onChange={this.onChange}
        onYearChange={this.onYearChange}
        onMonthChange={this.onMonthChange}
        onClose={this.onClose}
        InputProps={inputProps}
        PopoverProps={{className: "date-range-picker-popover"}}
      />
    </MuiPickersUtilsProvider>;
  }

  renderDay = (day: MaterialUiPickersDate, selectionStart: DateTime|null, selectionEnd: DateTime|null): JSX.Element => {
    let m = day as Moment;
    if(this.props.timeZone) {
      m = m.clone().tz(this.props.timeZone, true);
    }
    const displayedDate = this.state.displayedDate;
    const currentDay = this.state.currentDate;
    const disabled = this.shouldDisableDate(m);
    const isStartDate = (selectionStart && m.isSame(selectionStart.innerValue, 'day')) || false;
    const isEndDate = (selectionEnd && m.isSame(selectionEnd.innerValue, 'day')) || false;
    const isInRange = selectionStart && selectionEnd && m.isBetween(selectionStart.innerValue, selectionEnd.innerValue, 'day', '[]');
    const selected = isStartDate || isEndDate;
    // wrap day in span to add css class - Day has no className property
    let containerClassName = '';
    if(isInRange) {
      containerClassName += ' in-selection';
    }
    if(isStartDate) {
      containerClassName += ' selection-start';
    }
    if(isEndDate) {
      containerClassName += ' selection-end';
    }
    return <span className={containerClassName}>
      <Day
      selected={selected}
      current={m.isSame(currentDay, 'day')}
      hidden={!m.isSame(displayedDate, 'month')}
      disabled={disabled}>{m.format("D")}</Day>
    </span>;
  };

  renderValue = () => {
    const val = this.getValue();
    if(!val) {
      return '';
    } else if(val.start.equals(val.end, 'day')) {
      return val.start.format('MMM DD, YYYY');
    } else {
      return `${val.start.format('MMM DD, YYYY')} - ${val.end.format('MMM DD, YYYY')}`;
    }
  };

  onChange = (value: MaterialUiPickersDate) => {
    if(value && this.props.timeZone) {
      value = value.clone().tz(this.props.timeZone!, true);
    }
    const date = value ? new DateTime(value.clone().startOf('day')) : null;
    if(date) {
      const startDate = this.state.startDate;
      const endDate = this.state.endDate;
      if(startDate) {
        if(endDate) {
          // start and end date selected - deselect and use selection as start date
          this.setState({startDate: date, endDate: null});
        } else {
          // only start date selected - use selection as start or end date
          if(date.greaterThanOrEqual(startDate, 'day')) {
            // use as end date
            if(this.props.onChange) {
              this.props.onChange({start: startDate, end: date});
            }
            this.setState({endDate: date});
          } else {
            // use as start date
            if(this.props.onChange) {
              this.props.onChange({start: date, end: startDate});
            }
            this.setState({startDate: date, endDate: startDate});
          }
        }
      } else {
        // start date not selected - use selection as start date
        this.setState({startDate: date, endDate: null});
      }
    } else {
      // not date selected - clear selection
      if(this.props.onChange) {
        this.props.onChange(null);
      }
      this.setState({startDate: null, endDate: null});
    }
  };

  onClose = () => {
    // delay checking selection values, since onChange event is triggered after onClose
    setTimeout(this.afterClose, 1);
  };

  afterClose = () => {
    const startDate = this.state.startDate;
    if(startDate && !this.state.endDate) {
      if(this.props.onChange) {
        this.props.onChange({start: startDate, end: startDate});
      }
      this.setState({endDate: startDate});
    }
  }

  onYearChange = (date: MaterialUiPickersDate) => {
    let m = date as Moment;
    if(this.props.timeZone) {
      m = m.clone().tz(this.props.timeZone, true);
    }
    this.setState({displayedDate: m});
  };

  onMonthChange = (date: MaterialUiPickersDate) => {
    let m = date as Moment;
    if(this.props.timeZone) {
      m = m.clone().tz(this.props.timeZone, true);
    }
    this.setState({displayedDate: m});
  };

  getValue(): DateRange|null {
    if(this.props.value) {
      return this.props.value;
    } else if(this.state.startDate && this.state.endDate) {
      return { start: this.state.startDate, end: this.state.endDate};
    } else if(this.props.defaultValue) {
      return this.props.defaultValue;
    } else {
      return null;
    }
  }

  getPartialValue(): {start: DateTime|null, end: DateTime|null} {
    if(this.state.startDate && !this.state.endDate) {
      // picking end date - use state values
      return { start: this.state.startDate, end: null};
    } else if(this.props.value) {
      return this.props.value;
    } else if(this.state.startDate) {
      return { start: this.state.startDate, end: this.state.endDate};
    } else if(this.props.defaultValue) {
      return this.props.defaultValue;
    } else {
      return { start: null, end: null};
    }
  }

  shouldDisableDate = (date: MaterialUiPickersDate): boolean => {
    let m = date as Moment;
    if(this.props.timeZone) {
      m = m.clone().tz(this.props.timeZone, true);
    }
    let minDate = this.props.minDate;
    let maxDate = this.props.maxDate;
    const maxDuration = this.props.maxDuration;
    const startDate = this.state.startDate;
    if(maxDuration && startDate && !this.state.endDate) {
      // max duration is defined and user is selecting end date
      // clamp min/max dates to within duration of start date
      const minDurationDate = startDate.subtract(maxDuration);
      if(!minDate || minDate.lessThan(minDurationDate, 'day')) {
        minDate = minDurationDate;
      }
      const maxDurationDate = startDate.add(maxDuration);
      if(!maxDate || maxDate.greaterThan(maxDurationDate, 'day')) {
        maxDate = maxDurationDate;
      }
    }
    if(minDate && m.isBefore(minDate.innerValue, 'day')) {
      return true;
    } else if(maxDate && m.isAfter(maxDate.innerValue, 'day')) {
      return true;
    } else {
      return false;
    }
  };
}